import { Modal, ModalHeader, ModalBody, ModalFooter, Label } from '@windmill/react-ui';
import { FormEvent, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from 'src/components/_common/Button/Button';
import { Period } from 'src/models/classes';

interface PeriodFormModalProps {
  isOpen: boolean;
  close: () => void;
  isSaving: boolean;
  period?: Period;
  onSubmit: (period: Period) => void;
}

const PeriodFormModal = ({ isOpen, close, isSaving, period, onSubmit }: PeriodFormModalProps) => {
  const { formatMessage } = useIntl();
  const isEdit = period !== undefined;

  const [name, setName] = useState(period?.name ?? '');
  const [startDate, setStartDate] = useState(period?.startDate ?? '');
  const [endDate, setEndDate] = useState(period?.endDate ?? '');

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit({ id: period?.id ?? 0, name, startDate, endDate: endDate || undefined });
  };

  return (
    <Modal isOpen={isOpen} onClose={close} style={{ margin: 'auto' }}>
      <form onSubmit={handleSubmit}>
        <ModalHeader>
          <FormattedMessage id={isEdit ? 'edit-period' : 'create-period'} />
        </ModalHeader>
        <ModalBody>
          <Label className="flex flex-col mb-4">
            <span className="text-customGray">
              <FormattedMessage id="name" />
            </span>
            <input
              data-testid="period-name-input"
              className="border-0 shadow-tableShadow w-full"
              type="text"
              required
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </Label>
          <p className="mb-4">
            <FormattedMessage id="period-date-guidance" />
          </p>
          <Label className="flex flex-col mb-4">
            <span className="text-customGray">
              <FormattedMessage id="period-start-date" />
            </span>
            <input
              data-testid="period-start-date-input"
              className="border-0 shadow-tableShadow w-full"
              type="date"
              required
              max={endDate || undefined}
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
            />
          </Label>
          <Label className="flex flex-col">
            <span className="text-customGray">
              <FormattedMessage id="period-end-date" />
            </span>
            <input
              data-testid="period-end-date-input"
              className="border-0 shadow-tableShadow w-full"
              type="date"
              min={startDate || undefined}
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
            />
          </Label>
        </ModalBody>
        <ModalFooter>
          <Button className="w-full sm:w-auto" layout="outline" onClick={close}>
            <FormattedMessage id="cancel" />
          </Button>
          <Button
            data-testid="save-button"
            type="submit"
            text={<FormattedMessage id={isEdit ? 'save' : 'create'} />}
            className="w-full sm:w-auto"
            isSaving={isSaving}
            ariaLabel={formatMessage({ id: isEdit ? 'edit-period' : 'create-period' })}
          />
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default PeriodFormModal;
